<template>
  <div class="sf-el submission-data-element">
    <div v-if="label" class="submission-data-element__title">
      {{ label }}
    </div>
    <div
      class="submission-data-element__content"
      :class="{
        'submission-data-element__content--horizontal': layout === 'horizontal',
        'submission-data-element__content--vertical': layout === 'vertical',
      }"
    >
      <template v-if="Object.keys(filteredFormData).length > 0">
        <div
          v-for="(value, fieldId) in filteredFormData"
          :key="fieldId"
          class="submission-data-element__field"
        >
          <div v-if="showLabels" class="submission-data-element__label">
            {{ getFieldLabel(fieldId) }}:
          </div>
          <div class="submission-data-element__value">
            {{ formatFieldValue(fieldId, value) }}
          </div>
        </div>
      </template>
      <div v-else class="submission-data-element__empty">No submission data available yet</div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, computed } from 'vue';

  export default defineComponent({
    name: 'SubmissionDataElement',
    props: {
      label: {
        type: String,
        default: 'Submission summary',
      },
      showLabels: {
        type: Boolean,
        default: true,
      },
      showEmptyFields: {
        type: Boolean,
        default: false,
      },
      layout: {
        type: String,
        default: 'vertical',
        validator: (value) => ['vertical', 'horizontal'].includes(value),
      },
      formId: {
        type: String,
        required: true,
      },
      fieldId: {
        type: String,
        required: true,
      },
      // Additional props that FormElement passes
      modelValue: {
        type: [String, Number, Boolean, Array, Object],
        default: null,
      },
      formData: {
        type: Object,
        default: () => ({}),
      },
      form: {
        type: Object,
        default: () => ({}),
      },
    },
    setup(props) {
      const filteredFormData = computed(() => {
        if (!props.formData || typeof props.formData !== 'object') return {};

        const filtered = {};

        // Filter out current element's own data and empty values if needed
        Object.entries(props.formData).forEach(([key, value]) => {
          // Skip the current element itself
          if (key === props.fieldId) return;

          // Skip empty values if showEmptyFields is false
          if (
            !props.showEmptyFields &&
            (!value || value === '' || value === null || value === undefined)
          ) {
            return;
          }

          filtered[key] = value;
        });

        return filtered;
      });

      const getFieldElement = (fieldId) => {
        if (!props.form || !props.form.pages) return null;

        // Find the element in all pages
        for (const page of props.form.pages) {
          if (page.elements) {
            const element = page.elements.find((el) => el.id === fieldId);
            if (element) return element;
          }
        }

        // Also check ending pages
        if (props.form.ending_pages) {
          for (const page of props.form.ending_pages) {
            if (page.elements) {
              const element = page.elements.find((el) => el.id === fieldId);
              if (element) return element;
            }
          }
        }

        return null;
      };

      const getFieldLabel = (fieldId) => {
        const element = getFieldElement(fieldId);
        return element?.props?.label || fieldId;
      };

      const formatFieldValue = (fieldId, value) => {
        if (value === null || value === undefined) return '-';

        const element = getFieldElement(fieldId);
        const fieldType = element?.type;

        // Handle KvK fields
        if (fieldType === 'kvk' && typeof value === 'object') {
          const parts = [];
          if (value.businessInfo?.name) {
            parts.push(value.businessInfo.name);
          }
          if (value.kvkNumber) {
            parts.push(`KvK: ${value.kvkNumber}`);
          }
          if (parts.length > 0) {
            return parts.join(' - ');
          }
        }

        // Handle choice fields (single-choice, multiple-choice, picture-choice)
        if (
          ['single-choice', 'multiple-choice', 'picture-choice'].includes(fieldType) &&
          element?.props?.choices
        ) {
          const choices = element.props.choices;

          if (Array.isArray(value)) {
            // Multiple selections
            return value
              .map((id) => {
                const choice = choices.find((c) => c.id === id);
                return choice?.content || id;
              })
              .join(', ');
          } else {
            // Single selection
            const choice = choices.find((c) => c.id === value);
            return choice?.content || value;
          }
        }

        // Handle star rating
        if (fieldType === 'star-rating') {
          const maxStars = element?.props?.maxStars || 5;
          return `${value} out of ${maxStars} stars`;
        }

        // Handle arrays (like multiple choice selections without proper choice mapping)
        if (Array.isArray(value)) {
          return value.join(', ');
        }

        // Handle address objects with better formatting
        if (typeof value === 'object') {
          // For address objects, format nicely
          if (value.street && value.city) {
            const parts = [];
            if (value.street) parts.push(value.street);
            if (value.houseNumber) parts.push(value.houseNumber);
            const streetPart = parts.join(' ');

            const cityParts = [];
            if (value.postalCode) cityParts.push(value.postalCode);
            if (value.city) cityParts.push(value.city);
            const cityPart = cityParts.join(' ');

            return [streetPart, cityPart].filter(Boolean).join(', ');
          }

          // For other objects, show key-value pairs
          return Object.entries(value)
            .filter(([k, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ');
        }

        // Handle boolean values
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }

        return String(value);
      };

      return {
        filteredFormData,
        getFieldLabel,
        formatFieldValue,
      };
    },
  });
</script>

<style scoped lang="scss">
  .submission-data-element {
    padding: 1.5rem;
    background: var(--cs-input-default-background, #f8f9fa);
    border: 1px solid var(--cs-input-default-border, #dee2e6);
    border-radius: 0.5rem;
    margin: 1rem 0;

    &__title {
      font-size: 1rem;
      font-weight: 600;
      color: var(--cs-text-heading, #333);
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid var(--cs-input-default-border, #dee2e6);
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      &--horizontal {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 1rem;

        .submission-data-element__field {
          flex: 1;
          min-width: 200px;
        }
      }

      &--vertical {
        flex-direction: column;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__label {
      font-weight: 500;
      color: var(--cs-text-heading, #666);
      font-size: 0.875rem;
    }

    &__value {
      color: var(--cs-text-primary, #333);
      font-size: 0.95rem;
      padding: 0.5rem;
      background: var(--cs-background-white, #fff);
      border: 1px solid var(--cs-input-default-border, #dee2e6);
      border-radius: 0.25rem;
      word-break: break-word;
    }

    &__empty {
      text-align: center;
      color: var(--cs-text-secondary, #666);
      font-style: italic;
      padding: 2rem;
    }
  }

  // Editor context styles
  .sf-el--context-editor {
    .submission-data-element {
      &__content {
        min-height: 100px;
      }

      &__field {
        opacity: 0.7;
      }

      &__value {
        font-style: italic;
        color: var(--cs-text-secondary, #666);
      }

      &__empty {
        background: var(--cs-background-white, #fff);
        border: 1px dashed var(--cs-input-default-border, #dee2e6);
        border-radius: 0.25rem;
      }
    }
  }
</style>
